import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Globe, Loader2, Plus } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@solid/ui';
import { cn } from '@/lib/utils';
import { formsApi } from '@/lib/api';
import { NewFormModal } from '@/components/forms/NewFormModal';
import { FORM_STATUS_CLASSES, FORM_STATUS_LABELS, formatDateShort } from '@/components/forms/form-utils';

type Scope = 'all' | 'global' | 'project';

/** /forms/manage — the agency's library: every form I can see, where it lives, how it's doing. */
export function FormsManage() {
  const [params, setParams] = useSearchParams();
  const scope = (params.get('scope') as Scope) || 'all';
  const includeArchived = params.get('archived') === '1';
  const [showNew, setShowNew] = useState(false);

  const { data: forms, isLoading, isError, refetch } = useQuery({
    queryKey: ['formsManage', scope, includeArchived],
    queryFn: () => formsApi.manage({ scope, includeArchived }),
  });

  const setScope = (s: Scope) => {
    const next = new URLSearchParams(params);
    if (s === 'all') next.delete('scope');
    else next.set('scope', s);
    setParams(next, { replace: true });
  };
  const toggleArchived = () => {
    const next = new URLSearchParams(params);
    if (includeArchived) next.delete('archived');
    else next.set('archived', '1');
    setParams(next, { replace: true });
  };

  const grouped = useMemo(() => {
    const global = (forms ?? []).filter((f) => !f.projectId);
    const byProject = new Map<string, { name: string; forms: typeof global }>();
    for (const f of forms ?? []) {
      if (!f.projectId) continue;
      const g = byProject.get(f.projectId) ?? { name: f.project?.name || 'Project', forms: [] };
      g.forms.push(f);
      byProject.set(f.projectId, g);
    }
    return { global, projects: Array.from(byProject.entries()).sort((a, b) => a[1].name.localeCompare(b[1].name)) };
  }, [forms]);

  return (
    <div className="w-full">
      <PageHeader
        title="Manage forms"
        subtitle={
          <>
            Build questionnaires, assign them, and read the answers.{' '}
            <Link to="/forms" className="text-accent hover:underline">My forms</Link> shows what's been asked of you.
          </>
        }
        actions={
          <Button onClick={() => setShowNew(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New form
          </Button>
        }
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-lg border border-grey-200 p-0.5 bg-white" role="tablist" aria-label="Scope">
          {(['all', 'global', 'project'] as Scope[]).map((s) => (
            <button
              key={s}
              role="tab"
              aria-selected={scope === s}
              onClick={() => setScope(s)}
              className={cn('px-3 py-1.5 text-sm rounded-md transition-colors', scope === s ? 'bg-secondary text-white' : 'text-grey-600 hover:bg-grey-50')}
            >
              {s === 'all' ? 'All' : s === 'global' ? 'Global' : 'In projects'}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-grey-600 cursor-pointer ml-auto">
          <input type="checkbox" checked={includeArchived} onChange={toggleArchived} className="w-4 h-4 rounded text-accent border-grey-300 focus:ring-accent" />
          Show archived
        </label>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
        </div>
      ) : isError ? (
        <Card>
          <p className="text-sm text-secondary">Couldn't load forms.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
            Retry
          </Button>
        </Card>
      ) : !forms || forms.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={ClipboardList}
            title="No forms yet"
            description="Start with a global onboarding questionnaire, or build one inside a project from its Forms tab."
            action={
              <Button onClick={() => setShowNew(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New form
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.global.length > 0 && (
            <Group title="Global" icon={<Globe className="w-4 h-4 text-grey-400" />} hint="Assignment-only">
              {grouped.global.map((f) => (
                <FormRow key={f.id} form={f} />
              ))}
            </Group>
          )}
          {grouped.projects.map(([projectId, g]) => (
            <Group key={projectId} title={g.name} href={`/projects/${projectId}/forms`}>
              {g.forms.map((f) => (
                <FormRow key={f.id} form={f} />
              ))}
            </Group>
          ))}
        </div>
      )}

      {showNew && <NewFormModal onClose={() => setShowNew(false)} />}
    </div>
  );
}

function Group({ title, icon, hint, href, children }: { title: string; icon?: React.ReactNode; hint?: string; href?: string; children: React.ReactNode }) {
  return (
    <Card padding="none">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-grey-100 bg-grey-50/50">
        {icon}
        {href ? (
          <Link to={href} className="text-sm font-medium text-secondary hover:text-accent">
            {title}
          </Link>
        ) : (
          <span className="text-sm font-medium text-secondary">{title}</span>
        )}
        {hint && <span className="text-xs text-grey-400">· {hint}</span>}
      </div>
      <div className="divide-y divide-grey-100">{children}</div>
    </Card>
  );
}

function FormRow({ form }: { form: Awaited<ReturnType<typeof formsApi.manage>>[number] }) {
  const outstanding = form.counts.pending + form.counts.draft;
  return (
    <Link to={`/forms/manage/${form.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-grey-50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-secondary truncate">{form.title}</span>
          <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', FORM_STATUS_CLASSES[form.status])}>{FORM_STATUS_LABELS[form.status]}</span>
          {form.openSubmissions && <span className="text-xs text-grey-500">Open to members</span>}
        </div>
        <p className="text-xs text-grey-500 mt-0.5">
          {form.fields.filter((f) => f.type !== 'section').length} questions · updated {formatDateShort(form.updatedAt)}
          {form.createdBy?.name ? ` · by ${form.createdBy.name}` : ''}
        </p>
      </div>
      <div className="text-right text-xs text-grey-500 tabular-nums shrink-0">
        <div className="text-secondary font-medium">{form.counts.submitted} submitted</div>
        <div>{outstanding ? `${outstanding} outstanding` : '—'}</div>
      </div>
    </Link>
  );
}
